import {
  CalendarIcon,
  ClockIcon,
  DataTable,
  DateTimeInput,
  DeleteIcon,
  PlusIcon,
  TableChip,
  Tooltip,
} from "components";
import { useAtom, useSetAtom } from "jotai";
import { v4 as uuid } from "uuid";
import {
  assigneeList,
  cloneSchedule,
  deleteAssigneeItem,
  updateAssignee,
} from "../store/assigneeStore";
import {
  claculateTimeInBetween,
  convertUTCtoTimezoneV2,
  dateTimeToUtcV2,
  getFullName,
} from "utilz/helpers";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Dayjs } from "dayjs";
import { dayjs } from "utilz";
import { useGlobalStore } from "controller";
import { AssigneeTableData } from "../type/assigneeType";
import { ReactNode } from "react";

const headers = [
  {
    name: "Name",
    sortable: false,
    key: "fullname",
  },
  {
    name: "Start Date & Time",
    sortable: false,
    key: "startDate",
  },
  {
    name: "End Date & Time",
    sortable: false,
    key: "endDate",
  },
  {
    name: "Total Time",
    sortable: false,
    key: "totalTime",
  },
  {
    name: "",
    sortable: false,
    key: "action",
  },
];
type HandleTimeType = {
  rowData: AssigneeTableData;
  startTime: string | Dayjs | null;
  endTime: string | Dayjs | null;
};
export const AssigneeScheduleTable = () => {
  const [assigneesArray, _] = useAtom(assigneeList);
  const updateAssigneeTime = useSetAtom(updateAssignee);
  const deleteAssignee = useSetAtom(deleteAssigneeItem);
  const cloneScheduleFn = useSetAtom(cloneSchedule);
  const timezone = useGlobalStore((state) => state.timezone);
  const theme = useTheme();

  const handleScheduleDelete = (rowData: AssigneeTableData) => {
    deleteAssignee({
      ...rowData,
      schedule_uuid: rowData.uuid,
    });
  };

  const handleScheduleClone = ({
    rowData,
    index,
  }: {
    rowData: AssigneeTableData;
    index: number;
  }) => {
    cloneScheduleFn({
      ...rowData,
      uuid: uuid(),
      index,
    });
  };
  const handleTimeUpdate = ({
    rowData,
    startTime,
    endTime,
  }: HandleTimeType) => {
    updateAssigneeTime({
      ...rowData,
      schedule_uuid: rowData.uuid,
      start_time: startTime ? dateTimeToUtcV2(startTime, timezone) : null,
      end_time: endTime ? dateTimeToUtcV2(endTime, timezone) : null,
    });
  };

  const celleBuilders = {
    fullname: (rowData: AssigneeTableData) => {
      const fullName = getFullName(rowData.first_name, "", rowData.last_name);
      return (
        <Typography variant="body-small" textTransform={"capitalize"}>
          {fullName}
        </Typography>
      );
    },
    startDate: (rowData: AssigneeTableData) => {
      const hasErrors = rowData?.errors?.start_time ? true : false;
      return (
        <ErrorTooltip errorMessage={rowData?.errors?.start_time || null}>
          <DateTimeInput
            disablePast
            value={convertUTCtoTimezoneV2(`${rowData?.start_time}`, timezone)}
            timezone={timezone}
            error={hasErrors}
            onChangeHandler={(newValue) => {
              handleTimeUpdate({
                rowData,
                startTime: newValue,
                endTime: dayjs(newValue).add(1, "hour"),
              });
            }}
            sx={{
              ...theme.typography["body-small"],

              "& .MuiOutlinedInput-root": {
                height: {
                  sm: "24px",
                },

                color: hasErrors
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
                "& > fieldset": {
                  border: "none",
                  padding: "0px !important",
                },
              },

              "& .MuiButtonBase-root": {
                padding: 0,
                width: "12px",
                height: "12px",
                color: hasErrors
                  ? theme.palette.error.main
                  : theme.palette.lightBg.low,
              },
              "& .MuiInputBase-input": {
                padding: 0,
              },
            }}
          />
        </ErrorTooltip>
      );
    },
    endDate: (rowData: AssigneeTableData) => {
      const hasErrors = rowData?.errors?.end_time ? true : false;
      return (
        <ErrorTooltip errorMessage={rowData?.errors?.end_time || null}>
          <DateTimeInput
            disablePast
            // disabled={!rowData?.start_time}
            minDateTime={convertUTCtoTimezoneV2(
              `${rowData?.start_time}`,
              timezone,
            )}
            value={convertUTCtoTimezoneV2(`${rowData?.end_time}`, timezone)}
            error={hasErrors}
            onChangeHandler={(newValue) => {
              handleTimeUpdate({
                rowData,
                startTime: rowData?.start_time,
                endTime: newValue,
              });
            }}
            timezone={timezone}
            sx={{
              ...theme.typography["body-small"],
              "& .MuiOutlinedInput-root": {
                height: {
                  sm: "24px",
                },

                color: hasErrors
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
                "& > fieldset": {
                  border: "none",
                  padding: "0px !important",
                },
              },
              "& .MuiButtonBase-root": {
                padding: 0,
                color: hasErrors
                  ? theme.palette.error.main
                  : theme.palette.lightBg.low,
              },
              "& .MuiInputBase-input": {
                padding: 0,
              },
            }}
          />
        </ErrorTooltip>
      );
    },
    totalTime: (rowData: AssigneeTableData) => {
      if (!rowData?.start_time || !rowData?.end_time)
        return (
          <TableChip
            content=""
            type="success"
            startIcon={<ClockIcon fontSize="small" />}
          ></TableChip>
        );
      const totalTime = claculateTimeInBetween({
        startTime: rowData?.start_time,
        endTime: rowData?.end_time,
      });
      return (
        <TableChip
          content={totalTime}
          type={"success"}
          startIcon={<ClockIcon fontSize="small" />}
        />
      );
    },
    action: (rowData: AssigneeTableData, index?: number) => {
      return (
        <Stack direction={"row"} gap="24px">
          <DeleteIcon onClick={() => handleScheduleDelete(rowData)} />
          <PlusIcon
            onClick={() => handleScheduleClone({ rowData, index: index || 0 })}
          />
        </Stack>
      );
    },
  };
  return (
    <>
      <DataTable
        headers={headers}
        data={assigneesArray}
        cellBuilders={celleBuilders}
        multiselect={false}
        selectedItems={[]}
        currentPageNumber={0}
        itemsPerPage={0}
      ></DataTable>
      {/* <Typography
        variant="caption-xs"
        sx={{ color: "text.disabled", mt: 2, textAlign: "center" }}
      >
        {JSON.stringify(assigneesArray, null, 2)}
      </Typography> */}
    </>
  );
};

const ErrorTooltip = ({
  children,
  errorMessage,
}: {
  children: ReactNode;
  errorMessage: string | null;
}) => {
  if (errorMessage) {
    return (
      <Tooltip
        title={errorMessage}
        placement="top"
        // followCursor={true}
        PopperProps={{
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "red",
            },
          },
        }}
      >
        <Box>{children}</Box>
      </Tooltip>
    );
  }
  return <Box>{children}</Box>;
};
